/*
 * � 2025, NTT Data Group. All rights reserved. See LICENSE.txt file in the project root for third party licenses info.
 */

import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable } from "rxjs";
import { CalculationKpis } from "../models/http/calculator/calculation-kpis.model";
import { CalculatorFormData } from "../models/http/calculator/calculator-form-data.model";
import { CalculatorOptions } from "../models/http/calculator/calculator-options.model";
import { CalculatorResponse } from "../models/http/calculator/calculator-response.model";
import { CloudProviderOptions } from "../models/http/calculator/cloud-provider-options.model";
import { OnPremiseOptions } from "../models/http/calculator/on-premise-options.model";
import { CalculatorHttpService } from "./http/calculator-http.service";

@Injectable({
	providedIn: "root"
})
export class CalculatorService {
	private _calculation = new BehaviorSubject<CalculationKpis | null>(null);
	public calculation$ = this._calculation.asObservable();

	public calculationId: string | null = null;

	constructor(private _calculatorHttpService: CalculatorHttpService) {}

	public setCalculation(calculation: CalculationKpis | null) {
		this._calculation.next(calculation);
	}

	/**
	 * Retrieves initial calculator form data from the server.
	 *
	 * @returns An observable containing the calculator form data.
	 */
	public getCalculatorFormData(): Observable<CalculatorFormData> {
		return this._calculatorHttpService.getCalculatorFormData();
	}

	/**
	 * Retrieves area, region, cpu and gpu options for on-premise environments.
	 *
	 * @returns An observable containing the on-premise area and region options.
	 */
	public getOnPremiseOptions(): Observable<OnPremiseOptions> {
		return this._calculatorHttpService.getOnPremiseOptions();
	}

	/**
	 * Retrieves area, region and cpu options for a specified cloud provider.
	 *
	 * @param id - The unique identifier of the cloud provider.
	 * @returns An Observable that emits the cloud provider options.
	 */
	public getCloudProviderOptions(id: string): Observable<CloudProviderOptions> {
		return this._calculatorHttpService.getCloudProviderOptions(id);
	}

	/**
	 * Initiates the calculator with the specified options for a given tenant.
	 *
	 * @param tenantId - The unique identifier of the tenant.
	 * @param calculatorOptions - The options to configure the calculator.
	 * @returns An Observable that emits the response from the calculator service.
	 */
	public startCalculator(
		tenantId: string,
		calculatorOptions: CalculatorOptions
	): Observable<CalculatorResponse> {
		return this._calculatorHttpService.startCalculator(tenantId, calculatorOptions);
	}
}
